/* import __COLOCATED_TEMPLATE__ from './dropdown.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import type RouterService from '@ember/routing/router-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { helper } from '@ember/component/helper';

export type AudienceMode = 'everyone' | 'fin_audience' | 'custom';
interface Signature {
  Element: HTMLDivElement;
  Args: Args;
  Blocks: {
    customOptionRenderer: [];
  };
}

interface Args {
  action: any;

  showCustomOption?: boolean;
  showManageSegmentsLink?: boolean;
  showIcon?: boolean;
  emptyLabel?: string;
  disabled?: boolean;

  // For custom rules editor
  allowedUserRoles?: string[];
  useRolePredicateGroup?: boolean;
  onUpdatePredicates?: (predicates: any) => void;
  onUpdateRolePredicates?: (rolePredicates: any) => void;
}

export default class Dropdown extends Component<Signature> {
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare intl: any;
  @service declare permissionsService: any;
  @service declare router: RouterService;
  @service declare notificationsService: any;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    if (!this.aiContentSegmentsService.isLoading) {
      this.aiContentSegmentsService.loadSegments();
    }
  }

  get showCustomOption() {
    return this.args.showCustomOption !== false;
  }

  get showManageSegmentsLink() {
    return this.args.showManageSegmentsLink !== false;
  }

  get segments() {
    return this.aiContentSegmentsService.segments;
  }

  get selectedMode() {
    return this.args.action.audienceMode;
  }

  get selectedSegmentIds() {
    return this.args.action.selectedSegmentIds;
  }

  get showCustomRulesEditor() {
    return this.args.action.audienceMode === 'custom';
  }

  get dropdownLabel() {
    return this.args.emptyLabel || this._generateDropdownLabel();
  }

  private _generateDropdownLabel(): string {
    switch (this.args.action.audienceMode) {
      case 'everyone':
        return this.intl.t('common.audience-selector-dropdown.everyone');
      case 'custom':
        return this.intl.t('common.audience-selector-dropdown.custom');
      case 'fin_audience':
        return this._generateSegmentLabel();
      default:
        return this.intl.t('common.audience-selector-dropdown.everyone');
    }
  }

  private _generateSegmentLabel(): string {
    let selectedIds = this.args.action.selectedSegmentIds;

    if (!selectedIds.length) {
      return this.intl.t('common.audience-selector-dropdown.everyone');
    }

    let segments = this.aiContentSegmentsService.segments;
    let firstSegment = segments?.find((segment: any) => selectedIds[0] === Number(segment.id));

    if (!firstSegment) {
      return this.intl.t('common.audience-selector-dropdown.audience');
    }

    let firstSegmentName = firstSegment.nameWithFallback;
    let MAX_NB_CHARS = 30;

    if (firstSegmentName && firstSegmentName.length > MAX_NB_CHARS) {
      firstSegmentName = `${firstSegmentName.substring(0, MAX_NB_CHARS)}...`;
    }

    if (selectedIds.length === 1) {
      return firstSegmentName;
    }

    return this.intl.t('common.audience-selector-dropdown.additional-segments', {
      segmentName: firstSegmentName,
      count: selectedIds.length - 1,
    });
  }

  segmentIsAppliedToSelection = helper(
    ([segment, selectedSegmentIds]: [AiContentSegment, number[]]): boolean => {
      return selectedSegmentIds && selectedSegmentIds.includes(Number(segment.id));
    },
  );

  @action
  async selectEveryone() {
    if (this.args.action.audienceMode === 'everyone') {
      return;
    }
    this.args.action.switchToEveryone();
  }

  @action
  async selectCustom() {
    if (this.args.action.audienceMode === 'custom') {
      return;
    }
    this.args.action.switchToCustom();
  }

  @action
  async applySegment(segment: AiContentSegment) {
    this.args.action.toggleSegment(Number(segment.id));
  }

  @action
  redirectToAudienceSettings() {
    let url = this.router.urlFor('apps.app.settings.audiences', this.appService.app.id);
    safeWindowOpen(url, '_blank');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Common::AudienceSelectorDropdown::Dropdown': typeof Dropdown;
    'common/audience-selector-dropdown/dropdown': typeof Dropdown;
  }
}
